import { City, toAPIRequest } from "./city.model";
import { CityBuilder } from "./city.builder";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AngularFirestore } from "@angular/fire/firestore";
import { environment } from "src/environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../services";

export class CityFactory {
  public cities$: Observable<City[]>;
  public builder: CityBuilder;

  constructor(
    public db: AngularFirestore, 
    public httpClient: HttpClient,
    private authService: AuthService,
    public toast: ToastService,
  ) {
    this.getCitiesOnInitialization();
  }

  private async getCitiesOnInitialization(): Promise<void> {
    const userJWT = await this.authService.user.getIdToken();
    
    this.cities$ = this.httpClient.get<City[]>(`${environment.apiBaseUrl}/api/v1/cities`, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    }).pipe(
      map(citiesResponse => citiesResponse
        .map((cityResponse) => new CityBuilder().loadAPIResponse(cityResponse, this.db).build()))
    );
  }

  // getAllCities will query the backend to get a list of all cities without any scoping
  // or filtering.
  public async getAllCities(): Promise<City[]> {
    const userJWT = await this.authService.user.getIdToken();

    return this.httpClient.get<City[]>(`${environment.apiBaseUrl}/api/v1/cities`, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then(cities => 
        cities.map(city => new CityBuilder().loadAPIResponse(city, this.db).build())
      );
  }

  // create will create a new, blank instance of the CityBuilder.
  public create() {
    this.builder = new CityBuilder();
  }

  // toggle will accept a city, toggle the isActive property, and then
  // update it on the back and front-ends.
  public async toggle(city: City) {
    const toggledCity = new CityBuilder()
      .edit({
        ...city,
        isActive: !city.isActive,
      } as City)
      .build();
    
    await this._update(toggledCity);
  }

  // fix updates the state of the locally stored venue by recreating the underlying
  // CityBuilder.
  public fix(city: City) {
    this.builder = new CityBuilder().edit(city);
  }

  // deploy will either update or create the provided city with the backend,
  // depending on whether or not the provided city exists yet on the backend.
  public async deploy(city: City) {
    city.reference ? this._update(city) : this._add(city);
  }

  private async _update(city: City) {
    const userJWT = await this.authService.user.getIdToken();
    
    this.httpClient.put(`${environment.apiBaseUrl}/api/v1/cities`, {
      id: city.reference.id,
      data: {
        ...toAPIRequest(city),
      },
    }, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then((updateCityResponse) => {
        const updatedCity = new CityBuilder().loadAPIResponse(updateCityResponse as City, this.db).build();

        this.cities$.pipe(
          map((cities) => cities.map((city => {
            if (city.id === updatedCity.id) {
              return updatedCity;
            }

            return city;
          })))
        )
      })
      .catch((error) => {
        this.toast.error(
          `Failed to update ${city.name}. Please try again.`,
          "Error",
        );
      });
  }

  private async _add(city: City) {
    const userJWT = await this.authService.user.getIdToken();
    
    this.httpClient.post(`${environment.apiBaseUrl}/api/v1/cities`, {
      ...toAPIRequest(city),
    }, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then((createdCity) => {
        this.cities$.pipe(
          map(cities => cities.concat(
            new CityBuilder().loadAPIResponse(createdCity as City, this.db).build())
          )
        )
      })
      .catch((error) => {
        this.toast.error(
          `Failed to create ${city.name}. Please try again.`,
          "Error",
        );
      });
  }
}
